import moment from 'moment'
import { rem } from 'polished'
import React, { useEffect, useState } from 'react'
import { UseFormHandleSubmit, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import AddToItinerary from '../Common/Fields/AddToItinerary'

import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { useTrip } from 'tripPlanner/hooks/api'

// Styled Components
const StyledDiv = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  width: 100%;
`

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${mediaQueryUp.tablet} {
    gap: ${rem(16)};
    flex-direction: row;
    justify-content: flex-end;
  }
`

const MessageBannerContainer = styled.div`
  width: 100%;
  margin-bottom: ${rem(20)};
`

interface Props {
  onSubmit: ReturnType<UseFormHandleSubmit<any>>
  isLoading: boolean
  onCheckBoxChange?: () => void
  tripId?: string
  itineraryMessage?: string
}

function CreateModalFooterWithItinerary({
  onSubmit,
  isLoading,
  tripId,
  itineraryMessage = 'Please add dates for inclusion on the itinerary',
}: Props) {
  const { formState, watch, clearErrors, setValue, getValues } =
    useFormContext()
  const isInItinerary = watch('isInItinerary')

  useEffect(() => {
    if (!isInItinerary) {
      clearErrors(['startDate', 'endDate'])
    }
  }, [isInItinerary, clearErrors])

  const endDate = watch('endDate')
  const startDate = watch('startDate')

  const hasStartDateError =
    formState.errors.startDate !== undefined &&
    (startDate === null || startDate === undefined)
  const hasEndDateError =
    formState.errors.endDate !== undefined &&
    (endDate === null || endDate === undefined)
  const isMobile = useIsMobileScreen()

  const [submitClick, setSubmitClick] = useState(false)

  const { data: trip } = useTrip({ tripId })

  const tripHasDates = trip?.startDate && trip.endDate

  const showOutOfDateRangeWarning = () => {
    if (!trip?.startDate || !trip.endDate) {
      return false
    }

    const tripStart = moment(trip.startDate)
    const tripEnd = moment(trip.endDate)

    if (
      startDate &&
      !moment(startDate).isBetween(tripStart, tripEnd, 'day', '[]')
    ) {
      return true
    }

    if (
      endDate &&
      !moment(endDate).isBetween(tripStart, tripEnd, 'day', '[]')
    ) {
      return true
    }

    return false
  }

  // don't check the add to itinerary more than once.
  const [hasAutoChecked, setHasAutoChecked] = useState(false)

  useEffect(() => {
    const hasEndDate = 'endDate' in getValues()

    if (
      !hasAutoChecked &&
      startDate &&
      (!hasEndDate || (hasEndDate && endDate))
    ) {
      setValue('isInItinerary', true)
      setHasAutoChecked(true)
    }
  }, [startDate, endDate, hasAutoChecked, setValue, getValues])

  const onSubmitHandler = () => {
    setSubmitClick(true)
    onSubmit()
  }

  return (
    <ModalFooter>
      <StyledDiv>
        {(hasStartDateError || hasEndDateError) && submitClick && (
          <MessageBannerContainer>
            <MessageBanner
              description={itineraryMessage}
              heading=""
              icon={<LineInfoCircleIcon />}
              kind="critical"
            />
          </MessageBannerContainer>
        )}
        {showOutOfDateRangeWarning() && (
          <MessageBannerContainer>
            <MessageBanner
              description="Dates added to this item fall outside of your trip dates. You can update the trip dates in your trip settings."
              heading=""
              icon={<LineInfoCircleIcon />}
              kind="warning"
            />
          </MessageBannerContainer>
        )}
        <ActionContainer>
          {tripHasDates && <AddToItinerary message={itineraryMessage} />}
          <TextButton
            type="submit"
            data-testid="modal-primary-action"
            kind="primary"
            size="large"
            fit={`${isMobile ? 'full-width' : 'auto'}`}
            onClick={onSubmitHandler}
            disabled={isLoading}
          >
            Save
          </TextButton>
        </ActionContainer>
      </StyledDiv>
    </ModalFooter>
  )
}

export default CreateModalFooterWithItinerary
